<template>
    <form
        @change="isFormDirty"
        @submit.prevent="handleSubmit"
        :class="{ 'form-container': typeForm, 'form-modal-container': typeModal }"
        data-cy="form-container"
    >
        <p class="text-center text-default-red p-2" v-if="validation.message">
            {{ validation.message }}
        </p>
        <div class="form-section" data-cy="section-general">
            <div class="section-body">
                <div data-cy="field-report-name" class="input-container w-full px-4 mb-4">
                    <ui-label>{{ $t('report.form.labels.report_name') }}</ui-label>
                    <ui-input
                        :class="{ 'form-error': v$.report_template.report_name.$error }"
                        v-model="v$.report_template.report_name.$model"
                    />
                    <div
                        data-cy="field-report-name-error"
                        :class="{ 'text-default-red': v$.report_template.report_name.$error }"
                        v-if="v$.report_template.report_name.required.$invalid && v$.report_template.report_name.$error"
                    >
                        {{ $t('validation.required') }}
                    </div>
                    <validation-errors :errors="validation.errors.report_name" v-if="validation.errors" />
                </div>
                <div data-cy="field-report-module" class="input-container w-full px-4 mb-4">
                    <ui-label>{{ $t('report.form.labels.report_module') }}</ui-label>
                    <ui-select
                        :class="{ 'form-error': v$.report_template.report_module.$error }"
                        v-model="v$.report_template.report_module.$model"
                        :options="report_modules"
                    />
                    <div
                        data-cy="field-report-module-error"
                        :class="{ 'text-default-red': v$.report_template.report_module.$error }"
                        v-if="
                            v$.report_template.report_module.required.$invalid &&
                            v$.report_template.report_module.$error
                        "
                    >
                        {{ $t('validation.required') }}
                    </div>
                    <validation-errors :errors="validation.errors.report_module" v-if="validation.errors" />
                </div>
                <div data-cy="field-report-type" class="input-container w-full px-4 mb-4">
                    <ui-label>{{ $t('report.form.labels.report_type') }}</ui-label>
                    <ui-select
                        :class="{ 'form-error': v$.report_template.report_type.$error }"
                        v-model="v$.report_template.report_type.$model"
                        :options="report_types"
                    />
                    <div
                        data-cy="field-report-type-error"
                        :class="{ 'text-default-red': v$.report_template.report_type.$error }"
                        v-if="v$.report_template.report_type.required.$invalid && v$.report_template.report_type.$error"
                    >
                        {{ $t('validation.required') }}
                    </div>
                    <validation-errors :errors="validation.errors.report_type" v-if="validation.errors" />
                </div>
                <div data-cy="field-description" class="input-container w-full px-4 mb-4">
                    <ui-label>{{ $t('report.form.labels.description') }}</ui-label>
                    <ui-text-area
                        :class="{ 'form-error': v$.report_template.description.$error }"
                        v-model="v$.report_template.description.$model"
                    />
                    <validation-errors :errors="validation.errors.description" v-if="validation.errors" />
                </div>
                <div data-cy="field-filename" class="input-container w-full px-4 mb-4" v-if="report_template.filename">
                    <ui-label>{{ $t('report.form.labels.existing_filename') }}</ui-label>
                    <ui-input :disabled="true" v-model="v$.report_template.filename.$model" />
                </div>
                <div data-cy="field-report_file" class="input-container w-full px-4 mb-4">
                    <ui-label>{{ $t('report.form.labels.report_file') }}</ui-label>
                    <a-upload
                        accept=".rptdesign"
                        :file-list="file_list"
                        @remove="onFileRemove"
                        :before-upload="onBeforeUpload"
                    >
                        <a-button class="mt-2">
                            <UploadOutlined />
                            {{ $t('report.form.buttons.upload') }}
                        </a-button>
                    </a-upload>
                    <validation-errors :errors="validation.errors.report_file" v-if="validation.errors" />
                </div>
            </div>
        </div>
        <div class="form-section" data-cy="section-flags">
            <div class="section-body">
                <div data-cy="field-is-active" class="input-container lg:w-1/3 px-4">
                    <ui-checkbox v-model="report_template.is_active">
                        {{ $t('report.form.labels.is_active') }}
                    </ui-checkbox>
                </div>
            </div>
        </div>
        <div class="form-submit" v-if="form_type == 'form'">
            <ui-submit-button data-cy="submit-button" :disabled="processing">
                <span v-if="!processing">{{ $t('report.form.buttons.save') }}</span>
                <span v-else>{{ $t('report.form.buttons.saving') }}</span>
            </ui-submit-button>
        </div>
        <div class="drawer-footer" v-if="form_type == 'drawer'">
            <a-button data-cy="drawer-cancel-btn" class="btn-cancel" @click="onDrawerClose">
                {{ $t('report.form.buttons.cancel') }}
            </a-button>
            <a-button data-cy="drawer-save-btn" class="ant-btn-primary" :disabled="processing" @click="handleSubmit">
                <span v-if="!processing">{{ $t('report.form.buttons.save') }}</span>
                <span v-else>{{ $t('report.form.buttons.saving') }}</span>
            </a-button>
        </div>
    </form>
</template>

<script>
import UiCheckbox from '@/components/ui/Checkbox.vue';
import UiInput from '@/components/ui/Input.vue';
import UiLabel from '@/components/ui/Label.vue';
import UiSelect from '@/components/ui/Select.vue';
import UiSubmitButton from '@/components/ui/SubmitButton.vue';
import UiTextArea from '@/components/ui/TextArea.vue';

import ValidationErrors from '@/components/ui/ValidationErrors.vue';

import { UploadOutlined } from '@ant-design/icons-vue';

import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';

export default {
    setup() {
        return { v$: useVuelidate() };
    },
    components: {
        UiCheckbox,
        UiInput,
        UiTextArea,
        UiLabel,
        UiSelect,
        UiSubmitButton,
        ValidationErrors,
        UploadOutlined,
    },
    data() {
        return {
            report_types: [
                {
                    text: this.$t('report.form.selects.report_type.association'),
                    value: 'association',
                },
                {
                    text: this.$t('report.form.selects.report_type.company'),
                    value: 'company',
                },
                {
                    text: this.$t('report.form.selects.report_type.user'),
                    value: 'user',
                },
            ],
            report_modules: [
                {
                    text: this.$t('report.form.selects.report_module.claimsvault'),
                    value: 'claimsvault',
                },
                {
                    text: this.$t('report.form.selects.report_module.covervault'),
                    value: 'covervault',
                },
            ],
            file_list: [],
            report_file: null,
            image_updated: false,
        };
    },
    validations: {
        report_template: {
            report_name: {
                required,
            },
            report_module: {
                required,
            },
            report_type: {
                required,
            },
            filename: {},
            description: {},
            is_active: {},
        },
    },
    computed: {
        typeForm() {
            return this.form_type !== 'modal';
        },
        typeModal() {
            return this.form_type == 'modal';
        },
    },
    props: {
        mode: String,
        form_type: {
            type: String,
            default: 'drawer',
        },
        report_template: Object,
        processing: {
            type: Boolean,
            default: false,
        },
        validation: Object,
    },
    methods: {
        isFormDirty() {
            this.$emit('is-form-dirty', this.v$.$anyDirty);
        },
        onFileRemove(file) {
            this.file_list = [];
            this.report_file = null;
            this.image_updated = false;
        },
        onBeforeUpload(file) {
            let is_file_valid = true;

            // cannot check rptdesign file type but can check file size
            let mb = 1000000; // 1MB
            let extension = 'unknown';
            let file_split = file.name.split('.');

            if (file_split.length > 1) {
                extension = file_split[file_split.length - 1];
            }

            if (extension !== 'rptdesign') {
                is_file_valid = false;

                this.$notification.error({
                    message: this.$t('validation.mimes', { attribute: 'File', values: 'rptdesign' }),
                });
            }

            if (file.size >= mb) {
                is_file_valid = false;

                this.$notification.error({
                    message: this.$t('validation.lt.file', { attribute: 'File', value: 1024 }),
                });
            }

            if (is_file_valid) {
                this.image_updated = true;
                this.file_list = [file];
                this.report_file = file;
            }

            return false;
        },
        async handleSubmit() {
            if (!(await this.v$.$validate())) return false;

            const form_data = new FormData();

            for (var key in this.report_template) {
                if (key != 'report_file' && key != 'filename') {
                    form_data.append(key, this.report_template[key]);
                }

                if (key == 'is_active') {
                    let is_active = this.report_template[key] == true ? 1 : 0;
                    form_data.set(key, is_active);
                }
            }

            if (this.image_updated) {
                form_data.append('report_file', this.report_file);
            }

            this.$emit('formSubmit', {
                unformatted_data: this.report_template,
                form_data: form_data,
            });
        },
        onDrawerClose() {
            this.$emit('onDrawerClose');
        },
    },
};
</script>
